import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Box,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

import { MENU_ITEMS, MenuItem } from '../constants/menu';
import { usePermission } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';

interface NavigationProps {
    open: boolean;
    onClose: () => void;
    mode: 'side' | 'over';
}

const Navigation: React.FC<NavigationProps> = ({ open, onClose, mode }) => {
    const location = useLocation();
    const { hasPermission, isSuperAdmin, currentAdmin, getCurrentAdmin } = usePermission();
    const { signOut, user } = useAuth();
    const [navigationItems, setNavigationItems] = useState<MenuItem[]>([]);
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

    // Auto-open group when current route matches a child link
    useEffect(() => {
        const autoOpen: Record<string, boolean> = {};
        MENU_ITEMS.forEach(item => {
            if (item.children) {
                const childActive = item.children.some(c => c.link === location.pathname);
                if (childActive) autoOpen[item.id] = true;
            }
        });
        setOpenGroups(prev => ({ ...prev, ...autoOpen }));
    }, [location.pathname]);

    useEffect(() => {
        const resolvedAdmin = currentAdmin || getCurrentAdmin();
        const userType = localStorage.getItem('userType');

        const filterItem = (item: MenuItem): boolean => {
            if (isSuperAdmin()) return true;
            if (item.id === 'dashboard') return true;
            if (userType !== 'admin') {
                if (!item.permission) return true;
                return ['profile', 'change-password'].includes(item.permission);
            }
            if (!item.permission) return true;
            const menuPermissions = resolvedAdmin?.menu_permission
                || (resolvedAdmin as any)?.menuPermissions
                || (resolvedAdmin as any)?.menuPermission
                || [];
            if (menuPermissions.includes('*') || menuPermissions.length === 0) return true;
            return hasPermission(item.permission);
        };

        const filtered = MENU_ITEMS.filter(filterItem).map(item => {
            if (item.children) {
                return { ...item, children: item.children.filter(filterItem) };
            }
            return item;
        });

        setNavigationItems(filtered);
    }, [hasPermission, isSuperAdmin, currentAdmin, getCurrentAdmin, user, location.pathname]);

    const toggleGroup = (id: string) => {
        setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const isActive = (link?: string) => !!link && location.pathname === link;

    const renderItem = (item: MenuItem) => {
        // Group with children
        if (item.children && item.children.length > 0) {
            const groupActive = item.children.some(c => isActive(c.link));
            const isOpen = openGroups[item.id] ?? false;

            return (
                <React.Fragment key={item.id}>
                    <ListItem disablePadding className="mb-1">
                        <ListItemButton
                            onClick={() => toggleGroup(item.id)}
                            className={`p-[8px] md:py-2.5 md:px-4 transition-all rounded-[6px] text-white ${groupActive ? 'bg-[#0EA5E9] text-white shadow-lg' : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'}`}
                        >
                            <ListItemIcon className={`min-w-[32px] ${groupActive ? 'text-white' : 'text-[#94A3B8]'}`}>
                                <div className="text-xl flex items-center justify-center">{item.icon}</div>
                            </ListItemIcon>
                            <ListItemText
                                primary={item.title}
                                primaryTypographyProps={{
                                    style: {
                                        fontSize: '14px',
                                        fontWeight: groupActive ? 700 : 500,
                                        color: groupActive ? '#ffffff' : 'inherit',
                                    }
                                }}
                            />
                            {isOpen
                                ? <ExpandLess sx={{ color: groupActive ? '#fff' : '#94A3B8', fontSize: 20 }} />
                                : <ExpandMore sx={{ color: groupActive ? '#fff' : '#94A3B8', fontSize: 20 }} />
                            }
                        </ListItemButton>
                    </ListItem>

                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List disablePadding className="pl-3 mb-1">
                            {item.children.map(child => (
                                <ListItem key={child.id} disablePadding className="mb-1">
                                    <ListItemButton
                                        component={NavLink}
                                        to={child.link!}
                                        end
                                        className={`p-[8px] md:py-2 md:px-4 transition-all rounded-[6px] text-white ${isActive(child.link) ? 'bg-[#0EA5E9] text-white shadow-lg' : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'}`}
                                        onClick={mode === 'over' ? onClose : undefined}
                                        sx={{
                                            '&.active': {
                                                backgroundColor: '#0EA5E9',
                                                '& .MuiListItemIcon-root': { color: 'white' }
                                            }
                                        }}
                                    >
                                        <ListItemIcon className={`min-w-[28px] ${isActive(child.link) ? 'text-white' : 'text-[#94A3B8]'}`}>
                                            <div className="text-lg flex items-center justify-center">{child.icon}</div>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={child.title}
                                            primaryTypographyProps={{
                                                style: {
                                                    fontSize: '13px',
                                                    fontWeight: isActive(child.link) ? 700 : 500,
                                                    color: isActive(child.link) ? '#ffffff' : 'inherit',
                                                }
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>
                </React.Fragment>
            );
        }

        // Regular flat item
        return (
            <ListItem key={item.id} disablePadding className="mb-1">
                <ListItemButton
                    {...(item.id === 'logout'
                        ? { onClick: signOut }
                        : { component: NavLink, to: item.link! })}
                    className={`p-[8px] md:py-2.5 md:px-4 transition-all rounded-[6px] text-white ${isActive(item.link) ? 'bg-[#0EA5E9] text-white shadow-lg' : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'}`}
                    onClick={item.id === 'logout' ? signOut : (mode === 'over' ? onClose : undefined)}
                    sx={{
                        '&.active': {
                            backgroundColor: '#0EA5E9',
                            '& .MuiListItemIcon-root': { color: 'white' }
                        }
                    }}
                >
                    <ListItemIcon className={`min-w-[32px] ${isActive(item.link) ? 'text-white' : 'text-[#94A3B8]'}`}>
                        <div className="text-xl flex items-center justify-center">{item.icon}</div>
                    </ListItemIcon>
                    <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                            style: {
                                fontSize: '14px',
                                fontWeight: isActive(item.link) ? 700 : 500,
                                color: isActive(item.link) ? '#ffffff' : 'inherit',
                            }
                        }}
                    />
                </ListItemButton>
            </ListItem>
        );
    };

    const drawerContent = (
        <div className="bg-gray-800 w-full min-h-screen h-full flex flex-col">
            {/* Logo */}
            <div className="flex items-center justify-center h-20 p-6 pb-0 mb-[20px] flex-shrink-0">
                <img
                    className="w-30 md:w-40"
                    src="/assets/images/logo/logo.svg"
                    alt="codeflix invoice Logo"
                />
            </div>

            {/* Navigation List - Scrollable */}
            <div className="flex-1 overflow-y-auto py-2 px-[12px] custom-scrollbar pb-10">
                <List className="py-0">
                    {navigationItems.map(renderItem)}
                </List>
            </div>
        </div>
    );

    if (mode === 'over') {
        return (
            <Drawer
                anchor="left"
                open={open}
                onClose={onClose}
                ModalProps={{ keepMounted: true }}
            >
                <Box sx={{ width: 280 }}>
                    {drawerContent}
                </Box>
            </Drawer>
        );
    }

    return (
        <div className="h-full w-full border-r border-gray-700">
            {drawerContent}
        </div>
    );
};

export default Navigation;
